import CrudList from "../components/CrudList";
import { Car } from "@phosphor-icons/react";
import { StatusPill } from "../components/lux";
import { permVisible } from "../lib/permissions";
import { useAuth } from "../contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

const ownershipOptions = [
  { value: "company", label: "Company-owned" },
  { value: "third_party", label: "3rd-party owned" },
];

const splitTypeOptions = [
  { value: "percent", label: "Percentage % of net profit (after costs)" },
  { value: "percent_total", label: "Percentage % of total (gross, before costs)" },
  { value: "base_rate", label: "Base rate $ (owner takes base per trip, covers driver + gas)" },
];

const statusOptions = [
  { value: "available", label: "Available" },
  { value: "in_use", label: "In Use" },
  { value: "maintenance", label: "Maintenance" },
  { value: "retired", label: "Retired" },
];

const classOptions = [
  { value: "coupe", label: "Coupe" },
  { value: "sedan", label: "Sedan" },
  { value: "suv", label: "SUV" },
];

const serviceOptions = [
  { value: "both", label: "Rental + Chauffeur" },
  { value: "rental", label: "Rental Only" },
  { value: "chauffeur", label: "Chauffeur Only" },
];

const FIELDS = [
  { name: "make", label: "Make", required: true },
  { name: "model", label: "Model", required: true },
  { name: "year", label: "Year", type: "number" },
  { name: "license_plate", label: "License plate" },
  { name: "color", label: "Color" },
  { name: "vehicle_class", label: "Class", type: "select", options: classOptions, default: "sedan" },
  { name: "service_type", label: "Available for", type: "select", options: serviceOptions, default: "both" },
  { name: "status", label: "Status", type: "select", options: statusOptions, default: "available" },
  { name: "is_chauffeur_vehicle", label: "Chauffeur account vehicle", type: "boolean", hint: "Show this car to chauffeur-only user accounts", span: 2 },
  { name: "is_end_vehicle", label: "END-only vehicle", type: "boolean", hint: "When on, this car only appears under the END tab on Fleet Availability (hidden from Coupes / Sedans / SUVs)", span: 2 },
  { name: "affiliate_visible", label: "Show on affiliate availability", type: "boolean", default: true, hint: "When off, affiliate logins (car availability only accounts) can't see this car at all", span: 2 },
  { name: "show_on_website", label: "Show on public website (Shopify Book Now)", type: "boolean", hint: "When on, this vehicle appears in the public /book iframe and customers can request it directly.", span: 2 },
  { name: "website_name", label: "Website display name", placeholder: "e.g. 'Mercedes GLS Maybach' — leave blank to use Make + Model", span: 2 },
  { name: "image_url", label: "Website photo", type: "photo", span: 2 },
  { name: "daily_rate", label: "Daily rate", type: "number" },
  { name: "hourly_rate", label: "Hourly rate (chauffeur)", type: "number" },
  { name: "mileage", label: "Mileage / km", type: "number" },
  { name: "fuel_type", label: "Fuel" },
  { name: "transmission", label: "Transmission" },
  { name: "seats", label: "Seats", type: "number" },
  { name: "vin", label: "VIN" },
  { name: "insurance_expiry", label: "Insurance expiry", type: "date" },
  { name: "registration_expiry", label: "Registration expiry", type: "date" },
  { name: "last_service_date", label: "Last service", type: "date" },
  { name: "next_service_due", label: "Next service due", type: "date" },
  { name: "notes", label: "Notes", type: "textarea", span: 2 },
];

const statusColor = (s) =>
  s === "available" ? "text-primary" : s === "maintenance" ? "text-destructive" : s === "in_use" ? "text-foreground" : "text-muted-foreground";

const COLUMNS = [
  { key: "vehicle", label: "Vehicle", render: (v) => <div><div className="font-heading">{v.make} {v.model}{v.is_chauffeur_vehicle && <span className="ml-2 px-1.5 py-0.5 text-[9px] tracking-wider uppercase bg-blue-500/20 text-blue-300 rounded-sm align-middle" data-testid={`chauffeur-badge-${v.id}`}>Chauffeur</span>}{v.is_end_vehicle && <span className="ml-2 px-1.5 py-0.5 text-[9px] tracking-wider uppercase bg-amber-500/20 text-amber-300 rounded-sm align-middle" data-testid={`end-badge-${v.id}`}>END</span>}{v.show_on_website && <span className="ml-2 px-1.5 py-0.5 text-[9px] tracking-wider uppercase bg-emerald-500/20 text-emerald-300 rounded-sm align-middle" data-testid={`website-badge-${v.id}`}>Website</span>}</div><div className="text-xs text-muted-foreground">{v.year || "—"} · {v.color || "—"}</div></div> },
  { key: "license_plate", label: "Plate" },
  { key: "vehicle_class", label: "Class", render: (v) => <span className="capitalize">{v.vehicle_class || "—"}</span> },
  { key: "status", label: "Status", render: (v) => <span className={`label-mono ${statusColor(v.live_status || v.status)}`}>{v.live_status || v.status}</span> },
  { key: "daily_rate", label: "Daily rate", render: (v) => v.daily_rate ? `$${v.daily_rate}` : "—" },
];

const FLEET_FILTERS = [
  { key: "all", label: "All" },
  { key: "available", label: "Available", test: (v) => (v.live_status || v.status) === "available" },
  { key: "in_use", label: "In use", test: (v) => (v.live_status || v.status) === "in_use" },
  { key: "maintenance", label: "Maintenance", test: (v) => (v.live_status || v.status) === "maintenance" },
  { key: "chauffeur", label: "Chauffeur", test: (v) => v.is_chauffeur_vehicle },
  { key: "third_party", label: "3rd party", test: (v) => v.ownership === "third_party" },
];

const imgSrc = (u) => (!u ? "" : u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${u}` : u);

const renderVehicleCard = (v, { onEdit }) => (
  <button type="button" onClick={onEdit} className="text-left w-full h-full" data-testid={`vehicle-card-open-${v.id}`}>
    <div className="border border-border bg-card h-full overflow-hidden flex flex-col hover:border-primary/30 transition-colors duration-200">
      <div className="h-32 bg-white/[0.03] flex items-center justify-center overflow-hidden relative">
        {v.image_url ? (
          <img src={imgSrc(v.image_url)} alt={`${v.make} ${v.model}`} className="w-full h-full object-cover" />
        ) : (
          <Car size={26} className="text-muted-foreground/40" />
        )}
        <span className="absolute top-2 right-2"><StatusPill status={v.live_status || v.status} /></span>
      </div>
      <div className="p-4 flex flex-col">
        <div className="font-heading text-base truncate">{v.make} {v.model}</div>
        <div className="text-xs text-muted-foreground truncate mt-0.5">
          {[v.year, v.color, v.vehicle_class].filter(Boolean).join(" · ") || "—"}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap mt-3">
          {v.is_chauffeur_vehicle && <span className="px-2 py-0.5 rounded-full bg-sky-500/12 border border-sky-500/25 text-sky-300 text-[10px] uppercase tracking-wider" data-testid={`chauffeur-badge-${v.id}`}>Chauffeur</span>}
          {v.is_end_vehicle && <span className="px-2 py-0.5 rounded-full bg-amber-500/12 border border-amber-500/25 text-amber-300 text-[10px] uppercase tracking-wider" data-testid={`end-badge-${v.id}`}>END</span>}
          {v.affiliate_visible === false && <span className="px-2 py-0.5 rounded-full bg-rose-500/12 border border-rose-500/25 text-rose-300 text-[10px] uppercase tracking-wider" data-testid={`affiliate-hidden-badge-${v.id}`}>Affiliate hidden</span>}
          {v.show_on_website && <span className="px-2 py-0.5 rounded-full bg-emerald-500/12 border border-emerald-500/25 text-emerald-300 text-[10px] uppercase tracking-wider" data-testid={`website-badge-${v.id}`}>Website</span>}
          {v.ownership === "third_party" && <span className="px-2 py-0.5 rounded-full bg-violet-500/12 border border-violet-500/25 text-violet-300 text-[10px] uppercase tracking-wider">{v.owner_name || "3rd party"}</span>}
        </div>
        <div className="mt-4 pt-3 border-t border-border/60 flex items-end justify-between gap-3">
          <div>
            <div className="label-mono">Plate</div>
            <div className="text-sm font-mono mt-1">{v.license_plate || "—"}</div>
          </div>
          {v.daily_rate ? (
            <div className="text-right">
              <div className="label-mono">Per day</div>
              <div className="font-heading text-lg money mt-0.5">${v.daily_rate}</div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  </button>
);

export default function Fleet() {
  const { user } = useAuth();
  const { data: owners = [] } = useQuery({
    queryKey: ["car-owners"],
    queryFn: async () => (await api.get("/car-owners")).data,
    enabled: permVisible(user, "fleet"),
  });
  const ownerOptions = [{ value: "__none__", label: "— Company-owned —" }, ...owners.map((o) => ({ value: o.id, label: o.name }))];
  const fields = permVisible(user, "fleet")
    ? [
        ...FIELDS,
        { name: "owner_id", label: "Car owner (investor)", type: "select", options: ownerOptions, default: "__none__", hint: "Assign to a car owner from Settings → Car Owners. Their investor login will only see this car.", span: 2 },
        { name: "ownership", label: "Ownership", type: "select", options: ownershipOptions, default: "company" },
        { name: "owner_name", label: "3rd-party owner name (auto-fills from Car owner)" },
        { name: "owner_split_type", label: "Owner split type", type: "select", options: splitTypeOptions, default: "percent" },
        { name: "owner_split_percent", label: "Owner profit split % (percent type)", type: "number" },
      ]
    : FIELDS;
  return <CrudList endpoint="/vehicles" fields={fields} columns={COLUMNS} testid="vehicles" filters={FLEET_FILTERS} renderCard={renderVehicleCard} emptyText="No vehicles in fleet. Click 'New' or import from a CSV." />;
}
